const STORAGE_KEY = 'redevicer:imei-check-payment-v1'
const STORAGE_KEY_SESSION = 'redevicer:imei-check-payment-v1-session'
const PAID_RETURN_INFLIGHT_KEY = 'redevicer:imei-check-paid-inflight-v1'

export type PendingImeiCheckPayment = {
  imei: string
  service: string
  startedAt: string
}

export function storePendingImeiCheckPayment(payload: PendingImeiCheckPayment): void {
  const json = JSON.stringify(payload)
  try {
    localStorage.setItem(STORAGE_KEY, json)
  } catch {
    /* ignore quota */
  }
  try {
    sessionStorage.setItem(STORAGE_KEY_SESSION, json)
  } catch {
    /* ignore */
  }
}

export function readPendingImeiCheckPayment(): PendingImeiCheckPayment | null {
  const parse = (raw: string | null): PendingImeiCheckPayment | null => {
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as PendingImeiCheckPayment
      if (!parsed?.imei || !parsed?.service) return null
      return parsed
    } catch {
      return null
    }
  }
  return parse(localStorage.getItem(STORAGE_KEY)) ?? parse(sessionStorage.getItem(STORAGE_KEY_SESSION))
}

export function clearPendingImeiCheckPayment(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY_SESSION)
  } catch {
    /* ignore */
  }
}

export const IMEI_CHECK_PAID_EVENT = 'redevicer:imei-check-paid'

export type ImeiCheckPaidDetail = {
  imei: string
  service: string
  checkoutSessionId: string
}

const PAID_RETURN_KEY = 'redevicer:imei-check-paid-return-v1'
const PAID_SESSION_KEY = 'redevicer:imei-check-paid-session-v1'

export type StoredPaidCheckoutSession = {
  checkoutSessionId: string
  service: string
  storedAt: string
}

/** Stripe return without pending IMEI (e.g. storage cleared) — reuse session on next run. */
export function storePaidCheckoutSession(checkoutSessionId: string, service: string): void {
  try {
    localStorage.setItem(
      PAID_SESSION_KEY,
      JSON.stringify({
        checkoutSessionId,
        service,
        storedAt: new Date().toISOString(),
      } satisfies StoredPaidCheckoutSession),
    )
  } catch {
    /* ignore */
  }
}

export function takePaidCheckoutSession(service: string): string | null {
  try {
    const raw = localStorage.getItem(PAID_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredPaidCheckoutSession
    if (parsed.service !== service || !parsed.checkoutSessionId?.startsWith('cs_')) {
      return null
    }
    localStorage.removeItem(PAID_SESSION_KEY)
    return parsed.checkoutSessionId
  } catch {
    return null
  }
}

/** Survives Stripe redirect if the hub section is not mounted when the event fires. */
export function storePendingImeiCheckPaidReturn(detail: ImeiCheckPaidDetail): void {
  try {
    localStorage.setItem(PAID_RETURN_KEY, JSON.stringify(detail))
  } catch {
    /* ignore */
  }
}

export function consumePendingImeiCheckPaidReturn(): ImeiCheckPaidDetail | null {
  try {
    const raw = localStorage.getItem(PAID_RETURN_KEY)
    localStorage.removeItem(PAID_RETURN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ImeiCheckPaidDetail
    if (!parsed?.imei || !parsed?.service || !parsed?.checkoutSessionId) return null
    return parsed
  } catch {
    return null
  }
}

export function dispatchImeiCheckPaid(detail: ImeiCheckPaidDetail): void {
  storePendingImeiCheckPaidReturn(detail)
  window.dispatchEvent(new CustomEvent(IMEI_CHECK_PAID_EVENT, { detail }))
}

/** One post-checkout check per Stripe session (event + consumePending both fire on return). */
export function claimImeiCheckPaidReturnRun(checkoutSessionId: string): boolean {
  const sid = checkoutSessionId.trim()
  if (!sid.startsWith('cs_')) return false
  try {
    if (sessionStorage.getItem(PAID_RETURN_INFLIGHT_KEY) === sid) return false
    sessionStorage.setItem(PAID_RETURN_INFLIGHT_KEY, sid)
    return true
  } catch {
    return true
  }
}

export function releaseImeiCheckPaidReturnRun(checkoutSessionId: string): void {
  const sid = checkoutSessionId.trim()
  try {
    if (sessionStorage.getItem(PAID_RETURN_INFLIGHT_KEY) === sid) {
      sessionStorage.removeItem(PAID_RETURN_INFLIGHT_KEY)
    }
  } catch {
    /* ignore */
  }
}
